import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { useCountdown } from "../../hooks/useCountdown";
import { confirmDialog } from "../../../dialogs";
import { showToast } from "../../../toast";
import { cancelGiftApi, editGiftUnlockApi, openGiftViewer } from "../../../giftsApi";
import { UnlockDateTimePicker } from "./UnlockDateTimePicker";
import { OpenTogetherWaitingRoom } from "./OpenTogetherWaitingRoom";
import { initials } from "../chat/chatShared";

const MIN_LOCK_LEAD_MINUTES = 5;

function minUnlockParts(): { date: string; time: string } {
  const min = new Date(Date.now() + MIN_LOCK_LEAD_MINUTES * 60 * 1000);
  return { date: min.toISOString().slice(0, 10), time: min.toTimeString().slice(0, 5) };
}

// Locked/countdown detail page for a Private Drop - a real route
// (/gifts/drop/:id), not a dialog, matching this app's existing split
// (list/send/detail pages = routes; the actual reveal ceremony = a
// bespoke full-screen dialog, GiftViewerDialog.tsx). Reuses the exact
// ambient dark-burgundy background recipe built for the Sign In redesign
// (.auth-bg) rather than a new one - same "premium sealed" visual
// language, no new gradient to invent. V2 additions: an Open Together
// waiting room once unlocked-but-not-started, and sender-only cancel/edit
// while still locked.
export function PrivateDropLockedPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const gifts = useAppState((s) => s.gifts);
  const gift = gifts.find((g) => g.id === id) ?? null;
  const countdown = useCountdown(gift?.locked ? gift.unlockAt : null);
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const container = getVanillaEl<HTMLDivElement>("privateDropView");
  if (!container) return null;

  if (!gift) {
    return createPortal(
      <div className="auth-bg private-drop-locked-page">
        <div className="private-drop-locked-missing">
          <p>This Private Drop couldn't be found.</p>
          <button type="button" className="btn btn-pink btn-sm" onClick={() => navigate("/gifts")}>
            Back to Gifts
          </button>
        </div>
      </div>,
      container,
    );
  }

  // Unlocked, not yet opened, and set up for a synchronized reveal - the
  // waiting room takes over from here (it owns joining the Open Together
  // room; the actual reveal is opened globally the instant the server
  // confirms both sides are present, see GiftsScreen.tsx). Once opened, or
  // for a plain (non-Open-Together) ready drop, there's nothing for this
  // page to add - back to the list, which already shows the right card.
  if (!gift.locked) {
    if (gift.openTogether && !gift.viewedAt) {
      return createPortal(
        <div className="auth-bg private-drop-locked-page">
          <button type="button" className="private-drop-locked-back" aria-label="Back to Gifts" onClick={() => navigate("/gifts")}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
          </button>
          <OpenTogetherWaitingRoom gift={gift} />
        </div>,
        container,
      );
    }
    const canOpen = gift.direction === "received" && !gift.viewedAt;
    return createPortal(
      <div className="auth-bg private-drop-locked-page">
        <div className="private-drop-locked-missing">
          <p>This Private Drop has unlocked.</p>
          <button type="button" className="btn btn-pink btn-sm" onClick={() => (canOpen ? openGiftViewer(gift.id) : navigate("/gifts"))}>
            {canOpen ? "Open it" : "Back to Gifts"}
          </button>
        </div>
      </div>,
      container,
    );
  }

  const unlockDate = gift.unlockAt ? new Date(gift.unlockAt) : null;
  const isSender = gift.direction === "sent";

  async function handleCancel(): Promise<void> {
    const ok = await confirmDialog({
      title: "Cancel this drop?",
      message: "This Private Drop hasn't been opened yet - cancelling deletes it permanently and it won't be sent.",
      confirmLabel: "Cancel Drop",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    const result = await cancelGiftApi(gift!.id);
    setBusy(false);
    if (!result.ok) {
      showToast(result.error || "Failed to cancel drop.", "error");
      return;
    }
    showToast("Private Drop cancelled", "success");
    navigate("/gifts");
  }

  function startEditing(): void {
    const min = minUnlockParts();
    setEditDate(min.date);
    setEditTime(min.time);
    setEditError(null);
    setEditing(true);
  }

  async function handleSaveEdit(): Promise<void> {
    const parsedMs = Date.parse(`${editDate}T${editTime}`);
    if (!editDate || !editTime || !Number.isFinite(parsedMs) || parsedMs - Date.now() < MIN_LOCK_LEAD_MINUTES * 60 * 1000) {
      setEditError("Choose a time in the future.");
      return;
    }
    setBusy(true);
    const result = await editGiftUnlockApi(gift!.id, new Date(parsedMs).toISOString());
    setBusy(false);
    if (!result.ok) {
      setEditError(result.error || "Failed to update unlock time.");
      return;
    }
    setEditing(false);
    showToast("Unlock time updated", "success");
  }

  return createPortal(
    <div className="auth-bg private-drop-locked-page">
      <button type="button" className="private-drop-locked-back" aria-label="Back to Gifts" onClick={() => navigate("/gifts")}>
        <i className="bi bi-arrow-left" aria-hidden="true" />
      </button>

      <div className="private-drop-locked-content">
        <span className="private-drop-locked-envelope" aria-hidden="true">
          <i className="bi bi-envelope-heart-fill" />
          <i className="bi bi-lock-fill private-drop-locked-envelope-lock" />
        </span>
        <h1 className="private-drop-locked-title">Private Drop</h1>
        <p className="private-drop-locked-from">
          <span className="private-drop-locked-partner-avatar" aria-hidden="true">
            {initials(isSender ? gift.recipientName : gift.senderName)}
          </span>
          {isSender ? `To ${gift.recipientName}` : `From ${gift.senderName}`}
        </p>

        {editing ? (
          <UnlockDateTimePicker
            dateValue={editDate}
            timeValue={editTime}
            minDate={minUnlockParts().date}
            error={editError}
            onDateChange={setEditDate}
            onTimeChange={setEditTime}
          />
        ) : (
          unlockDate && (
            <div className="private-drop-locked-unlock">
              <span className="private-drop-locked-unlock-label">Unlocks</span>
              <span className="private-drop-locked-unlock-date">
                {unlockDate.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="private-drop-locked-unlock-time">
                at {unlockDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
          )
        )}

        {!editing && <p className="private-drop-locked-note">This drop will stay locked until the date and time above.</p>}
        {!editing && <span className="private-drop-locked-countdown">{countdown.label} remaining</span>}

        {isSender && (
          <div className="private-drop-locked-sender-actions">
            {editing ? (
              <>
                <button type="button" className="btn btn-outline-pink btn-sm" onClick={() => setEditing(false)} disabled={busy}>
                  Back
                </button>
                <button type="button" className="btn btn-pink btn-sm" onClick={handleSaveEdit} disabled={busy}>
                  Save
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-outline-pink btn-sm" aria-label="Edit unlock time" onClick={startEditing} disabled={busy}>
                  <i className="bi bi-pencil" aria-hidden="true" /> Edit unlock time
                </button>
                <button type="button" className="btn btn-outline-danger btn-sm" aria-label="Cancel Drop" onClick={handleCancel} disabled={busy}>
                  <i className="bi bi-x-lg" aria-hidden="true" /> Cancel Drop
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    container,
  );
}
